<template>
  <div class="flex flex-col h-full bg-white">
    <!-- Header -->
    <div class="flex items-center justify-between p-4 border-b sticky top-0 bg-white z-10">
      <div class="flex items-center gap-3">
        <button
          class="p-2 -mr-2 hover:bg-surface-100 rounded-full text-surface-600"
          @click="$emit('cancel')"
        >
          <i class="pi pi-times text-xl"></i>
        </button>
        <h1 class="font-bold text-lg text-surface-800">עריכת {{ elementTypeLabel }}</h1>
      </div>
      <Button
        label="שמירה"
        icon="pi pi-check"
        size="small"
        @click="$emit('save', editedEl)"
      />
    </div>

    <!-- Editor Content -->
    <div class="flex-1 overflow-y-auto p-5 space-y-6">
      <!-- Label -->
      <div>
        <label class="block text-sm font-semibold text-surface-700 mb-2">כותרת השדה</label>
        <InputText
          v-model="editedEl.label"
          class="w-full"
          placeholder="הזינו כותרת"
        />
      </div>

      <!-- Helper Text (not for read_only) -->
      <div v-if="editedEl.type !== 'read_only'">
        <label class="block text-sm font-semibold text-surface-700 mb-2">
          טקסט עזר
          <span class="text-surface-400 font-normal">(אופציונלי)</span>
        </label>
        <InputText
          v-model="editedEl.helperText"
          class="w-full"
          placeholder="טקסט שיופיע כרמז"
        />
      </div>

      <!-- Content (for read_only) -->
      <div v-if="editedEl.type === 'read_only'">
        <label class="block text-sm font-semibold text-surface-700 mb-2">תוכן הטקסט</label>
        <Textarea
          v-model="editedEl.content"
          class="w-full"
          rows="6"
          autoResize
          placeholder="הזינו את תוכן הטקסט כאן..."
        />
      </div>

      <!-- Options (for checkboxes / multiple_choice) -->
      <div v-if="hasOptions">
        <div class="flex justify-between items-center mb-3">
          <label class="block text-sm font-bold text-surface-800">אפשרויות</label>
          <span class="text-xs text-primary-600 font-medium">{{ editedEl.options?.length || 0 }} אפשרויות</span>
        </div>

        <div class="space-y-3">
          <div
            v-for="(opt, idx) in editedEl.options"
            :key="idx"
            class="flex items-center gap-3 group"
          >
            <i class="pi pi-bars text-surface-300 text-lg"></i>
            <InputText
              :modelValue="opt"
              @update:modelValue="val => editedEl.options![idx] = val as string"
              class="flex-1"
            />
            <button
              class="p-2 text-surface-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              @click="editedEl.options!.splice(idx, 1)"
            >
              <i class="pi pi-trash text-base"></i>
            </button>
          </div>
        </div>

        <button
          class="mt-4 w-full py-3 border-2 border-dashed border-primary-200 text-primary-600 rounded-xl font-medium hover:bg-primary-50 transition-all flex items-center justify-center gap-2"
          @click="editedEl.options!.push(`אפשרות ${(editedEl.options?.length || 0) + 1}`)"
        >
          <i class="pi pi-plus text-lg"></i> הוספת אפשרות
        </button>
      </div>

      <!-- Required toggle (not for read_only) -->
      <div
        v-if="editedEl.type !== 'read_only'"
        class="pt-6 border-t border-surface-100 flex items-center justify-between"
      >
        <div>
          <p class="font-semibold text-surface-800">שדה חובה</p>
          <p class="text-xs text-surface-500 mt-1">חובה למלא לפני שליחה</p>
        </div>
        <InputSwitch v-model="editedEl.required" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import type { IConsentFormElement } from '@torim-app/types';
import { ConsentFormElementTypeEnum } from '@torim-app/types';

const props = defineProps<{
  element: IConsentFormElement;
}>();

defineEmits<{
  save: [element: IConsentFormElement];
  cancel: [];
}>();

const editedEl = ref<IConsentFormElement>(JSON.parse(JSON.stringify(props.element)));

const hasOptions = computed(() =>
  [ConsentFormElementTypeEnum.Checkboxes, ConsentFormElementTypeEnum.MultipleChoice].includes(editedEl.value.type),
);

const elementTypeLabel = computed(() => {
  const map: Record<ConsentFormElementTypeEnum, string> = {
    [ConsentFormElementTypeEnum.ShortText]: 'שדה טקסט',
    [ConsentFormElementTypeEnum.Checkboxes]: 'תיבות סימון',
    [ConsentFormElementTypeEnum.MultipleChoice]: 'בחירה יחידה',
    [ConsentFormElementTypeEnum.Signature]: 'חתימה',
    [ConsentFormElementTypeEnum.ReadOnly]: 'בלוק טקסט',
    [ConsentFormElementTypeEnum.Image]: 'תמונה',
  };
  return map[editedEl.value.type] || editedEl.value.type;
});
</script>
